import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, IconButton, Tooltip, Tabs, Tab, Switch, TextField, Typography,
  Paper, FormControlLabel, Stack, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import { get_one, create, update, delete_one } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../components/AuthProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';

// Modelo de permissões COERENTE com as rotas reais do /hermes.
// Cada "ação" da UI pode mapear para 1+ permissões do backend.
// "Ver" agrupa get_all + get (que nesse sistema são a mesma coisa na prática).
const MODULES = [
  { key: 'host', label: 'Hosts', actions: [
    { label: 'Ver', perms: ['get_all', 'get'] },
    { label: 'Criar', perms: ['create'] },
    { label: 'Editar', perms: ['update'] },
    { label: 'Excluir', perms: ['delete'] },
  ] },
  { key: 'backup', label: 'Backups', actions: [
    { label: 'Ver / baixar', perms: ['get_all', 'get'] },
    { label: 'Rodar backup', perms: ['create'] },
    { label: 'Excluir', perms: ['delete'] },
  ] },
  { key: 'tunnel', label: 'Conectividade (Túneis)', actions: [
    { label: 'Ver', perms: ['get_all', 'get'] },
    { label: 'Criar', perms: ['create'] },
    { label: 'Editar', perms: ['update'] },
    { label: 'Excluir', perms: ['delete'] },
  ] },
  { key: 'queue', label: 'Fila', actions: [
    { label: 'Ver fila', perms: ['get_all'] },
  ] },
  { key: 'log', label: 'Relatório de erros', actions: [
    { label: 'Ver', perms: ['get_all'] },
  ] },
  { key: 'audit', label: 'Log de auditoria', actions: [
    { label: 'Ver', perms: ['get_all'] },
  ] },
  { key: 'config', label: 'Configurações', actions: [
    { label: 'Editar', perms: ['update'] },
  ] },
  { key: 'role', label: 'Cargos', actions: [
    { label: 'Ver', perms: ['get_all', 'get'] },
    { label: 'Criar', perms: ['create'] },
    { label: 'Editar', perms: ['update'] },
    { label: 'Excluir', perms: ['delete'] },
  ] },
];

export default function Role() {
  const auth = useAuth();
  const { setErrors } = useFormError();
  const [refresh, setRefresh] = useState(false);
  const [form, setForm] = useState({ open: false, id: null });
  const [tab, setTab] = useState(0);
  const [description, setDescription] = useState('');
  const [allPerms, setAllPerms] = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!form.open) return;
    setTab(0);
    setErrors([]);
    api.get('/hermes/role/permissions/all').then((r) => setAllPerms(r.data || [])).catch(() => setAllPerms([]));
    if (form.id) {
      get_one('/hermes/role', form.id).then((r) => {
        setDescription(r.data.data.description || '');
        setSelected(new Set(r.data.data.permissions || []));
      });
    } else {
      setDescription('');
      setSelected(new Set());
    }
  }, [form]);

  // "host.create" -> id
  const idByDesc = useMemo(() => {
    const m = {};
    for (const p of allPerms) m[p.description] = p.id;
    return m;
  }, [allPerms]);

  const setMany = (ids, on) => setSelected((prev) => {
    const n = new Set(prev);
    ids.forEach((i) => (on ? n.add(i) : n.delete(i)));
    return n;
  });

  const submit = () => {
    const payload = { description, permissions: Array.from(selected) };
    const req = form.id ? update('/hermes/role', form.id, payload) : create('/hermes/role/create', payload);
    req.then((res) => {
      if (res?.data?.errors) { setErrors(res.data.errors); return; }
      setForm({ open: false, id: null });
      setRefresh((r) => !r);
    });
  };

  const remove = (id) => {
    if (!window.confirm('Remover este cargo?')) return;
    delete_one('/hermes/role', id).then((res) => {
      if (res?.data?.error) { window.alert(res.data.error); return; }
      setRefresh((r) => !r);
    });
  };

  const columns = [
    { name: 'id', label: 'ID', options: { display: false, filter: false } },
    { name: 'description', label: 'Cargo' },
    {
      name: 'actions', label: 'Ações',
      options: {
        filter: false, sort: false,
        setCellProps: () => ({ align: 'right' }),
        setCellHeaderProps: () => ({ align: 'right' }),
        customBodyRender: (_, meta) => {
          const id = meta.rowData[0];
          const isSystem = [1, 2, 3].includes(id);
          return (
            <Box display="flex" gap={0.25} justifyContent="flex-end">
              <Tooltip title="Editar">
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('role.update')}
                              onClick={() => setForm({ open: true, id })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isSystem ? 'Cargo do sistema' : 'Remover'}>
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('role.delete') || isSystem}
                              onClick={() => remove(id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <>
      <FormDialog
        open={form.open}
        title={form.id ? 'Editar cargo' : 'Novo cargo'}
        handlerClose={() => setForm({ open: false, id: null })}
        handlerSubmit={submit}
        btnSubmit={form.id ? 'Atualizar' : 'Salvar'}
        maxWidth="md"
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Geral" />
          <Tab label="Permissões" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ pt: 2 }}>
            <TextField label="Nome do cargo" fullWidth value={description}
                       onChange={(e) => setDescription(e.target.value)} />
          </Box>
        )}

        {tab === 1 && (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {MODULES.map((mod) => {
              // resolve cada ação -> ids existentes no backend
              const items = mod.actions
                .map((a) => ({
                  label: a.label,
                  ids: a.perms.map((s) => idByDesc[`${mod.key}.${s}`]).filter((x) => x != null),
                }))
                .filter((x) => x.ids.length);
              if (!items.length) return null;
              const allIds = items.flatMap((x) => x.ids);
              const allOn = allIds.every((i) => selected.has(i));
              const someOn = allIds.some((i) => selected.has(i));
              return (
                <Paper key={mod.key} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{mod.label}</Typography>
                    <FormControlLabel
                      label="Tudo"
                      control={
                        <Switch size="small" checked={allOn}
                                indeterminate={someOn && !allOn}
                                onChange={(e) => setMany(allIds, e.target.checked)} />
                      }
                    />
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {items.map((it, idx) => {
                      const on = it.ids.every((i) => selected.has(i));
                      return (
                        <FormControlLabel
                          key={idx}
                          sx={{ minWidth: 150, m: 0 }}
                          label={it.label}
                          control={
                            <Switch size="small" checked={on}
                                    onChange={(e) => setMany(it.ids, e.target.checked)} />
                          }
                        />
                      );
                    })}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </FormDialog>

      <Datatable
        refresh={refresh}
        handlerRefresh={() => setRefresh((r) => !r)}
        title="Cargos"
        endpoint="/hermes/role"
        options={{
          customToolbar: () => (
            <Tooltip title="Adicionar cargo">
              <IconButton disabled={!auth.hasPerm('role.create')}
                          onClick={() => setForm({ open: true, id: null })}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          ),
          customToolbarSelect: () => '',
        }}
        columns={columns}
      />
    </>
  );
}
